import { track } from 'src/lib/analytics/utils';
import StateObserver, { now, user } from 'src/StateObserver';
import { getCurrentScene } from 'src/lib/stateUtils';
import {
  Match2PowerupType,
  PuzzleResults,
  match2AppsFlyerLevel,
  match2BuruStreakRewards,
  match2CostAnalytics,
} from 'src/replicant/match2/match2.ruleset';
import { BlockGenAlgorithm, ExtendedMapDef } from './game/defs/map';
import {
  getBuruStreakPowerups,
  isMatch2TimedRewardActive,
} from 'src/replicant/match2/match2.getters';
import { round } from './odie';
import { getBlockGen } from './match2.util';
import { getRaceAnalytics } from './races/races.analytics';
import {
  canPlayLeague,
  getCurrentLeaguePhase,
} from 'src/replicant/match2/leagues/leagues.getters';
import { State } from 'src/replicant/State';
import { SceneID } from 'src/state/ui';
import { isStreakBattleRoyaleActive } from 'src/replicant/streakbattleroyale/streakbattleroyale.getters';
import { Stats } from 'src/game/stats';
import { getTournament } from './tournament/match2-tournament.utils';
import {
  getRegionFirstLevel,
  getRegionLastLevel,
} from 'src/replicant/match2/regions/regions.getters';
import { Match2Reward } from 'src/replicant/match2/rewards/match2-rewards.ruleset';
import {
  getSuperLollipopActivationCount,
  isSuperLollipopActive,
} from 'src/replicant/superLollipop/superLollipop.getters';
import { trackAppsFlyerLevelAchieved } from 'src/lib/gcinstant';
import { setPuzzleUserProps } from 'src/lib/setupUserProps';
import { getTreasureExpressPuzzleEventProperties } from 'src/replicant/treasureExpress/treasureExpress.getters';
import { shouldApplyMoveReduction } from './movereduction/movereduction.utils';
import { getGameScene } from './game/getGameScene';
import type {
  RescueLevelPuzzleFinishProps,
  RescueLevelPuzzleStartProps,
} from 'src/replicant/rescueLevel/rescueLevel.analytics';

export type PowerUps =
  | 'A'
  | 'B'
  | 'C'
  | 'AB'
  | 'AC'
  | 'BC'
  | 'ABC'
  | 'none'
  | 'D'
  | 'AD'
  | 'BD'
  | 'ABD';
type PuzzleResult = 'faile' | 'success' | 'quit'; // Typo in the schema, should be 'fail' instead of 'faile'

export type Match2PuzzleStartData = {
  level: number;
  puzzleMoves: number;
  startTimeStamp: number;
  startingBoosters: PowerUps;
  buruStreakValue: number;
  buruStreakId: number;
  startScene: SceneID;
  visitingFriend: boolean;
  rubberbandingStep: number;
  blockGenType: BlockGenAlgorithm;
  maxBuruStreak: number;
  isStreakQuestActive: boolean;
};

/**
 * Send PuzzleStart analytics event
 */
export function match2SendPuzzleStartAnalytics(
  selectedPowerups: Match2PowerupType[] = [],
  mapDef: ExtendedMapDef,
  puzzleLevel: number,
  rescueLevelPuzzleStartProperties?: RescueLevelPuzzleStartProps,
) {
  const puzzleStartData = getPuzzleStartData(
    puzzleLevel,
    mapDef,
    selectedPowerups,
  );

  // Send puzzle start analytics data
  track('PuzzleStart', {
    ...puzzleStartData,
    ...rescueLevelPuzzleStartProperties,
  });

  return puzzleStartData;
}

export type Match2PuzzleFinishAnalyticsProps = ReturnType<
  typeof match2SendPuzzleFinishAnalytics
>;
/**
 * Send PuzzleFinish analytics event
 */
export function match2SendPuzzleFinishAnalytics(
  selectedPowerups: Match2PowerupType[] = [],
  puzzleStartData: PuzzleStartData,
  puzzleResults: PuzzleResults,
  puzzleLevel: number,
  puzzleAttempts: number,
  rescueLevelPuzzleFinishProperties?: RescueLevelPuzzleFinishProps,
  dynamicBlockGenType?: BlockGenAlgorithm,
  isCardDrop = false,
) {
  const racesAnalytics = getRaceAnalytics();

  // Get attempts stats
  const attemptsStats = Stats.api.getPuzzleAttemptsStats(user());

  // Get puzzle finish basic analytics data
  const puzzleFinishData = getPuzzleFinishData(
    puzzleLevel,
    selectedPowerups,
    puzzleStartData,
    puzzleResults,
    puzzleAttempts,
    dynamicBlockGenType,
  );

  const eventProperties = {
    ...puzzleFinishData,
    ...(racesAnalytics.properties ?? {}),
    // Race position needs to be up to date, so we call it here.
    racePosition: racesAnalytics.getPosition?.(),
    usingSaveState: puzzleResults.usingSaveState,
    isCardDrop,
    ...attemptsStats,
  };

  // Send puzzle finish analytics event with basic plus additional properties
  track('PuzzleFinish', {
    ...eventProperties,
    ...rescueLevelPuzzleFinishProperties,
  });

  // Update puzzle-related user properties after PuzzleFinish event
  setPuzzleUserProps();

  const hasWon = puzzleResults.result === 'win';

  // Do not track buru streak from rescue level puzzles
  if (!rescueLevelPuzzleFinishProperties) {
    // STREAK - Check if there is a new buru streak
    const newBuruStreak = hasWon
      ? Math.min(
          puzzleStartData.maxBuruStreak,
          puzzleStartData.buruStreakValue + 1,
        )
      : 0;

    // STREAK - Send burustreak analytics event
    if (hasWon) {
      track('BuruStreakAdvance', {
        currentStreak: newBuruStreak,
        usingSaveState: puzzleResults.usingSaveState,
      });

      // We only track the AppsFlyer level achieved event once the user reaches a specific level.
      if (eventProperties.completedLevel === match2AppsFlyerLevel) {
        trackAppsFlyerLevelAchieved({
          level: eventProperties.completedLevel,
          score: eventProperties.finalScore,
        });
      }
    } else {
      track('BuruStreakEnd', {
        pastStreak: puzzleStartData.buruStreakValue,
        usingSaveState: puzzleResults.usingSaveState,
      });
    }
  }

  return eventProperties;
}

/**
 * Get puzzle start analytics data
 */
export function getPuzzleStartData(
  level: number,
  mapDef: ExtendedMapDef,
  selectedPowerups: Match2PowerupType[] = [],
) {
  const startingPowerups = [];
  const user = StateObserver.getState().user;
  const rocket = selectedPowerups.includes('rocket');
  const bomb = selectedPowerups.includes('bomb');
  const cube = selectedPowerups.includes('cube');
  const startTimeStamp = StateObserver.now();
  let startingBoosters: PowerUps = 'none';

  if (rocket) {
    startingPowerups.push('firecracker');
  }

  if (bomb) {
    startingPowerups.push('waterbaloon');
  }

  if (cube) {
    startingPowerups.push('lollipop');
  }

  const boosters: string[] = [];
  if (rocket) boosters.push('A');
  if (bomb) boosters.push('B');
  if (cube) boosters.push(isSuperLollipopActive(user) ? 'D' : 'C');
  if (boosters.length > 0) {
    startingBoosters = boosters.join('') as PowerUps;
  }

  const maxBuruStreak = match2BuruStreakRewards.length;
  const startingBuruStreakValue = Math.min(
    user.match2.buruStreak.value,
    maxBuruStreak,
  );

  const blockGenType = getBlockGen(mapDef, level);

  const puzzleMoves = mapDef.moves;

  // Get attempts stats
  const attemptsStats = Stats.api.getPuzzleAttemptsStats(user);

  // Could be in the masters league where these are not tracked
  const allTimeAttemptCount = attemptsStats.absoluteLevelSequence
    ? attemptsStats.absoluteLevelSequence - 1
    : undefined;

  return {
    level,
    puzzleMoves,
    startTimeStamp,
    startingBoosters,
    buruStreakValue: startingBuruStreakValue,
    buruStreakId: user.match2.buruStreak.id,
    startScene: getCurrentScene(),
    visitingFriend: !!user.visit.visitSession,
    rubberbandingStep: user.match2.winLadderStep,
    blockGenType,
    maxBuruStreak,
    ...getLevelVersioningProps(level, mapDef),
    ...getLeagueProps(user),
    isStreakQuestActive: isStreakBattleRoyaleActive(user, startTimeStamp),
    ...attemptsStats,
    allTimeAttemptCount,
    isSuperLollipopActive: isSuperLollipopActive(user),
    superLollipopStreakID: isSuperLollipopActive(user)
      ? getSuperLollipopActivationCount(user)
      : undefined,
    isReducedMoves: shouldApplyMoveReduction(),
    wasSuperLollipopLostThisLevel:
      !!user.superLollipop?.lostSuperLollipopThisLevel,
    ...getTreasureExpressPuzzleEventProperties(user, now()),
  };
}

export type PuzzleStartData = ReturnType<typeof getPuzzleStartData>;

/**
 * Get puzzle finish analytics data
 */
export function getPuzzleFinishData(
  level: number,
  selectedPowerups: Match2PowerupType[] = [],
  puzzleStartData: ReturnType<typeof getPuzzleStartData>,
  puzzleResults: PuzzleResults,
  puzzleAttempts: number,
  dynamicBlockGenType?: BlockGenAlgorithm,
) {
  const user = StateObserver.getState().user;
  const now = StateObserver.now();
  const result =
    puzzleResults.result === 'quit' ? 'lose' : puzzleResults.result;
  const hasWon = result === 'win';
  let resultAnalytics: PuzzleResult =
    puzzleResults.result === 'win'
      ? 'success'
      : puzzleResults.result === 'quit'
        ? 'quit'
        : 'faile'; // TODO: Incorrect spelling here - fix after the test so prevent an analytics misalignment
  const newBuruStreak = hasWon
    ? Math.min(
        puzzleStartData.maxBuruStreak,
        puzzleStartData.buruStreakValue + 1,
      )
    : 0;

  const { moves, blocksBrokenAnalytics } = puzzleResults;

  const puzzleElapsedTime = Math.round(
    (StateObserver.now() - puzzleStartData.startTimeStamp) * 0.001,
  );

  // Get attempts stats
  const attemptsStats = Stats.api.getPuzzleAttemptsStats(user);

  // Could be in the masters league where these are not tracked
  const allTimeAttemptCount = attemptsStats.absoluteLevelSequence
    ? attemptsStats.absoluteLevelSequence
    : undefined;

  // Add the fail state to the out of moves counter
  const outOfMovesCount =
    puzzleResults.usedContinues + moves.remaining > 0 ? 0 : 1;

  return {
    result: resultAnalytics,
    completedLevel: level,
    startingBoosters: puzzleStartData.startingBoosters,
    usedAutoShuffles: puzzleResults.usedAutoShuffles,
    limitMoves: moves.limit,
    successMoves: moves.used,
    puzzleElapsedTime,
    remainingMoves: moves.remaining,
    socialContinueMoves: puzzleResults.socialContinueMoves,
    buruStreakValue: newBuruStreak,
    buruStreakId: puzzleStartData.buruStreakId,
    rubberbandingStep: puzzleStartData.rubberbandingStep,
    blockGenType: puzzleStartData.blockGenType,
    dynamicBlockGenType,
    starsEarned: puzzleResults.score?.stars,
    finalScore: puzzleResults.score?.points,
    percentScore: puzzleResults.score?.percentage,
    tournamentPosition: getTournament()?.getCached()?.currentRank,
    totalObstaclesRemaining: puzzleResults.totalObstaclesRemaining,
    ratioObstaclesRemaining:
      puzzleResults.totalObstaclesRemaining / puzzleResults.totalObstacleGoals,
    losingStreak: user.match2.losingStreak,

    regionLevelRange: puzzleStartData.regionLevelRange,
    regionVersion: puzzleStartData.regionVersion,
    regionCanonical: puzzleStartData.regionCanonical,
    regionHash: puzzleStartData.regionHash,

    puzzleLevel: puzzleStartData.puzzleLevel,
    puzzleVersion: puzzleStartData.puzzleVersion,
    puzzleCanonical: puzzleStartData.puzzleCanonical,
    puzzleHash: puzzleStartData.puzzleHash,

    puzzleMoves: puzzleStartData.puzzleMoves,

    startingMoves: moves.limit,
    usedMoves: moves.used,
    isStreakQuestActive: isStreakBattleRoyaleActive(user, now),

    allTimeAttemptCount,
    outOfMovesCount,

    // Use the data from the puzzleStart. We don't want to track the user's current super lollipop streak, but the streak they had when they started the puzzle.
    isSuperLollipopActive: puzzleStartData.isSuperLollipopActive,
    superLollipopStreakID: puzzleStartData.superLollipopStreakID,

    isReducedMoves: puzzleStartData.isReducedMoves,
    wasSuperLollipopLostThisLevel:
      puzzleStartData.wasSuperLollipopLostThisLevel,

    ...getLeagueProps(user),
    ...getPuzzleCostAnalytics(selectedPowerups, puzzleResults),
    ...getBoosterUsedAnalytics(puzzleResults),
    ...getSpendAnalytics(puzzleResults),
    ...puzzleResults.goalProgressAnalytics,
    ...blocksBrokenAnalytics,
    ...getTreasureExpressPuzzleEventProperties(user, now, puzzleAttempts),
  };
}

/**
 * Get master's league props.
 *
 * @param user
 * @returns
 */
export function getLeagueProps(user: State) {
  const leagueProps: { isLeagueLevel?: true; phase?: number } = {};
  if (canPlayLeague(user)) {
    leagueProps.isLeagueLevel = true;
    leagueProps.phase = getCurrentLeaguePhase(user);
  }

  return leagueProps;
}

/**
 * Track frenzy level complete
 */
export function trackFrenzyLevelComplete(data: {
  eventID: string;
  level: number;
  rewardType?: Match2Reward['type'];
}) {
  track('FrenzyLevelComplete', { gameType: 'match2', ...data });
}

/**
 * Get level versioning props.
 * @param puzzleLevel
 * @param map
 * @returns
 */
export function getLevelVersioningProps(
  puzzleLevel: number,
  map?: ExtendedMapDef,
) {
  if (!map) return {};

  const regionFirstLevel = getRegionFirstLevel(puzzleLevel);
  const regionLastLevel = getRegionLastLevel(puzzleLevel);
  const regionLevelRange = `${regionFirstLevel}-${regionLastLevel}`;
  let { regionVersion, regionHash, levelHash, levelVersion } = map;

  return {
    regionLevelRange,
    regionVersion,
    regionCanonical: getRegionCanonical(puzzleLevel, regionVersion),
    regionHash,

    puzzleLevel,
    puzzleVersion: levelVersion,
    puzzleCanonical: getPuzzleCanonical(puzzleLevel, levelVersion),
    puzzleHash: levelHash,
  };
}

/**
 * Get region canonical.
 * @param puzzleLevel
 * @param regionVersion
 */
export function getRegionCanonical(puzzleLevel: number, regionVersion: string) {
  const regionFirstLevel = getRegionFirstLevel(puzzleLevel);
  const regionLastLevel = getRegionLastLevel(puzzleLevel);
  const regionLevelRange = `${regionFirstLevel}-${regionLastLevel}`;
  return `${regionLevelRange}/v${regionVersion}`;
}

/**
 * Get puzzle canonical.
 * @param puzzleLevel
 * @param levelVersion
 */
export function getPuzzleCanonical(puzzleLevel: number, levelVersion: string) {
  return `${puzzleLevel}/v${levelVersion}`;
}

/**
 *
 * @param selectedPowerups
 * @param puzzleResults
 * @returns
 */
export function getPuzzleCostAnalytics(
  selectedPowerups: Match2PowerupType[],
  puzzleResults: PuzzleResults,
) {
  let puzzleCost = 0;
  let puzzleCostConsumable = 0;

  const combinedPowerups = [
    ...selectedPowerups,
    ...getBuruStreakPowerups(user()),
  ];

  // Add costs for starting powerups
  combinedPowerups.forEach((type) => {
    switch (type) {
      case 'rocket':
        puzzleCost += match2CostAnalytics.rocketCost;
        break;
      case 'bomb':
        puzzleCost += match2CostAnalytics.bombCost;
        break;
      case 'cube':
        puzzleCost += match2CostAnalytics.cubeCost;
        break;
    }
  });

  selectedPowerups
    .filter(
      (type) => !isMatch2TimedRewardActive(user(), `powerups-${type}`, now()),
    )
    .forEach((type) => {
      switch (type) {
        case 'rocket':
          puzzleCostConsumable += match2CostAnalytics.rocketCost;
          break;
        case 'bomb':
          puzzleCostConsumable += match2CostAnalytics.bombCost;
          break;
        case 'cube':
          puzzleCostConsumable += match2CostAnalytics.cubeCost;
          break;
      }
    });

  const { usedHammer, usedAirplaner, usedCuprocket, usedPinwheel } =
    getBoosterUsedAnalytics(puzzleResults);

  // Add costs for used boosters
  puzzleCost += usedHammer * match2CostAnalytics.dartCost;
  puzzleCost += usedAirplaner * match2CostAnalytics.bulletCost;
  puzzleCost += usedCuprocket * match2CostAnalytics.drillCost;
  puzzleCost += usedPinwheel * match2CostAnalytics.rouletteCost;
  puzzleCostConsumable += usedHammer * match2CostAnalytics.dartCost;
  puzzleCostConsumable += usedAirplaner * match2CostAnalytics.bulletCost;
  puzzleCostConsumable += usedCuprocket * match2CostAnalytics.drillCost;
  puzzleCostConsumable += usedPinwheel * match2CostAnalytics.rouletteCost;

  // Add costs for used continues
  if (puzzleResults.usedContinues > 0) {
    puzzleCost +=
      puzzleResults.usedContinues < 5
        ? puzzleResults.usedContinues * match2CostAnalytics.continueCost
        : match2CostAnalytics.continueCostCap;
  }

  // final factoring
  puzzleCost = round(puzzleCost / match2CostAnalytics.costFactor, 2);

  return { puzzleCost, puzzleCostConsumable };
}

/**
 * Get booster used analytics.
 * @param puzzleResults
 * @returns
 */
export function getBoosterUsedAnalytics(puzzleResults: PuzzleResults) {
  const usedBoosters = puzzleResults.usedBoosters;
  const usedHammer = usedBoosters.dart ?? 0;
  const usedAirplaner = usedBoosters.bullet ?? 0;
  const usedCuprocket = usedBoosters.drill ?? 0;
  const usedPinwheel = usedBoosters.roulette ?? 0;
  const boostersUsed =
    usedHammer + usedAirplaner + usedCuprocket + usedPinwheel;

  return {
    usedContinue: puzzleResults.usedContinues,
    boostersUsed,
    usedHammer,
    usedAirplaner,
    usedCuprocket,
    usedPinwheel,

    usedBoosters: boostersUsed,
    usedBoosterHammer: usedHammer,
    usedBoosterAirplane: usedAirplaner,
    usedBoosterCuprocket: usedCuprocket,
    usedBoosterPinwheel: usedPinwheel,
  };
}

/**
 * Get spend analytics.
 * @param puzzleResults
 * @returns
 */
export function getSpendAnalytics(puzzleResults: PuzzleResults) {
  const continueSpendAnalytics: {
    continueSpend?: number;
    continueSpendType?: 'Free' | 'Paid' | 'Mixed';
  } = {};

  if (puzzleResults.usedContinues) {
    const { continueSpend, adContinueMoves, socialContinueMoves } =
      puzzleResults;
    continueSpendAnalytics.continueSpend = continueSpend;

    let type = 0;

    const isPaid = continueSpend > 0;
    const isFree = adContinueMoves + socialContinueMoves > 0;

    continueSpendAnalytics.continueSpendType =
      isPaid && isFree ? 'Mixed' : isPaid ? 'Paid' : 'Free';
  }

  const boosterSpendAnalytics: {
    boosterSpend?: number;
    boosterSpendType?: 'Free' | 'Paid' | 'Mixed';
  } = {};

  if (puzzleResults.boosterSpendType) {
    const { boosterSpend, boosterSpendType } = puzzleResults;
    boosterSpendAnalytics.boosterSpend = boosterSpend;
    boosterSpendAnalytics.boosterSpendType = boosterSpendType;
  }

  const totalSpendAnalytics: {
    totalSpend?: number;
    totalSpendType?: 'Free' | 'Paid' | 'Mixed';
  } = {};

  if (
    boosterSpendAnalytics.boosterSpendType ||
    continueSpendAnalytics.continueSpendType
  ) {
    const continueSpend = continueSpendAnalytics.continueSpend ?? 0;
    const boosterSpend = boosterSpendAnalytics.boosterSpend ?? 0;
    const continueType =
      continueSpendAnalytics.continueSpendType ??
      boosterSpendAnalytics.boosterSpendType;
    const boosterType =
      boosterSpendAnalytics.boosterSpendType ??
      continueSpendAnalytics.continueSpendType;

    totalSpendAnalytics.totalSpend = continueSpend + boosterSpend;
    totalSpendAnalytics.totalSpendType =
      continueType === boosterType ? continueType : 'Mixed';
  }

  return {
    ...continueSpendAnalytics,
    ...boosterSpendAnalytics,
    ...totalSpendAnalytics,
  };
}
